import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { Loader2, Eraser } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { ContentBrief, ContentBriefDraft } from "@/lib/composerCore";
import { saveUserEditRevision } from "@/lib/briefRevisions";
import { SaveStatusPill } from "./DraftCanvasParts";
import type { RevisionSaveState } from "./draftCanvasTypes";
import { TITLE_DEBOUNCE_MS, docToTitle } from "./draftCanvasUtils";

type CorpusDocSummary = {
  id: string;
  title: string;
  chunk_count?: number;
  pinned?: boolean;
};

type DraftHeaderProps = {
  draft: ContentBriefDraft;
  brief: ContentBrief | null;
  saveState?: RevisionSaveState;
  saveError?: string | null;
  onTitleSaved?: (draftId: string, title: string) => void;
  /** Latest in-memory doc, used to derive a placeholder when title is empty. */
  latestDoc: unknown;
  onNewDraft?: () => void;
  /** Seed a new user_edit revision from a read-only AI draft and switch to it. */
  onSaveAsEditable?: () => void | Promise<void>;
  /** True while the save-as-editable request is in-flight. */
  savingAsEditable?: boolean;
  /** Optional extra action buttons rendered to the right of "+ New Draft". */
  extraActions?: ReactNode;
};

export const DraftHeader = ({
  draft,
  brief,
  saveState,
  saveError,
  onTitleSaved,
  latestDoc,
  onNewDraft,
  onSaveAsEditable,
  savingAsEditable,
  extraActions,
}: DraftHeaderProps) => {
  const isUserEdit = draft.revision_kind === "user_edit";
  const [title, setTitle] = useState<string>(draft.draft_title ?? "");
  const lastDraftIdRef = useRef(draft.id);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (lastDraftIdRef.current !== draft.id) {
      lastDraftIdRef.current = draft.id;
      setTitle(draft.draft_title ?? "");
    }
  }, [draft.id, draft.draft_title]);

  useEffect(() => {
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, []);

  const placeholder = docToTitle(latestDoc) || brief?.title || "Untitled draft";

  const scheduleSave = (next: string) => {
    if (!isUserEdit) return;
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      void saveUserEditRevision({ draftId: draft.id, title: next.trim() || null })
        .then(() => onTitleSaved?.(draft.id, next.trim()))
        .catch(() => {
          /* surfaced via parent autosave state when body changes; title-only failure is rare */
        });
    }, TITLE_DEBOUNCE_MS);
  };

  return (
    <div className="flex flex-wrap items-start gap-x-3 gap-y-1">
      <div className="flex min-w-0 flex-1 flex-col">
        {isUserEdit ? (
          <Input
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              scheduleSave(e.target.value);
            }}
            placeholder={placeholder}
            className="h-9 w-full border-transparent bg-transparent px-2 text-base font-semibold shadow-none focus-visible:border-input focus-visible:bg-background"
            aria-label="Draft title"
          />
        ) : (
          <span className="px-2 text-base font-semibold">
            {draft.draft_title || brief?.title || "Draft"}
          </span>
        )}
        <span className="-mt-0.5 px-2 text-xs text-muted-foreground">
          {isUserEdit ? "your edited revision" : "AI draft (read-only)"}
        </span>
        <CorpusInContextRow draft={draft} />
      </div>
      <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
        <SaveStatusPill state={saveState} error={saveError} />
        {!isUserEdit && onSaveAsEditable ? (
          <Button
            size="sm"
            variant="default"
            onClick={() => void onSaveAsEditable()}
            disabled={savingAsEditable}
          >
            {savingAsEditable ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : null}
            {savingAsEditable ? "Saving…" : "Save as Editable Draft"}
          </Button>
        ) : null}
        {onNewDraft ? (
          <Button
            size="sm"
            variant="outline"
            onClick={onNewDraft}
            title="Clear the canvas without deleting any saved drafts. Reopen saved drafts from the Drafts list on the right."
            aria-label="Clear canvas"
          >
            <Eraser className="mr-1 h-3.5 w-3.5" />
            Clear canvas
          </Button>
        ) : null}
        {extraActions}
      </div>
    </div>
  );
};

/**
 * Small row under the title/caption showing which Corpus docs the writer
 * actually had in context for this draft. Reads from `draft.context_snapshot.
 * corpus_document_titles` written by `content-draft-brief`. Shows a muted
 * "No Corpus docs in context" when the writer ran without any so users
 * catch the gap before regenerating.
 */
const CorpusInContextRow = ({ draft }: { draft: ContentBriefDraft }) => {
  const snap = useMemo<Record<string, unknown> | null>(() => {
    const raw = (draft as unknown as { context_snapshot?: unknown }).context_snapshot;
    return raw && typeof raw === "object" ? (raw as Record<string, unknown>) : null;
  }, [draft]);

  const docs = useMemo<CorpusDocSummary[]>(() => {
    if (!snap) return [];
    const raw = snap.corpus_document_titles;
    if (!Array.isArray(raw)) return [];
    return raw.filter(
      (d): d is CorpusDocSummary =>
        !!d && typeof d === "object" && typeof (d as CorpusDocSummary).id === "string" && typeof (d as CorpusDocSummary).title === "string",
    );
  }, [snap]);

  const pinnedDocCount = typeof snap?.corpus_pinned_doc_count === "number" ? snap.corpus_pinned_doc_count : 0;
  const corpusHits = typeof snap?.corpus_hits === "number" ? snap.corpus_hits : 0;
  // After the 2026-07-13 retrieval fix (publish-type filter removed, planner
  // pin backfill), a nonzero pinned list should ALWAYS produce nonzero hits.
  // If it doesn't, something upstream regressed — surface it loudly instead
  // of letting the writer hallucinate silently.
  const pinnedMissedTripwire = pinnedDocCount > 0 && corpusHits === 0;

  // Only show for AI drafts — user-edit revisions inherit their parent
  // AI draft's context and don't rerun retrieval.
  if (draft.revision_kind === "user_edit") return null;

  if (docs.length === 0) {
    return (
      <div className="mt-1 flex flex-col gap-1 px-2">
        <span className="text-[11px] italic text-muted-foreground">
          No Corpus docs in context for this draft
        </span>
        {pinnedMissedTripwire ? (
          <span className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[11px] font-medium text-amber-700 dark:text-amber-300">
            ⚠ {pinnedDocCount} Corpus doc{pinnedDocCount === 1 ? "" : "s"} pinned but 0 chunks landed in context. Retrieval likely misconfigured — regenerate; if this persists, report it.
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div className="mt-1 flex flex-wrap items-center gap-1 px-2">
      <span className="text-[11px] text-muted-foreground">Corpus in context:</span>
      {docs.map((d) => (
        <span
          key={d.id}
          title={`${d.chunk_count ?? 0} chunk${d.chunk_count === 1 ? "" : "s"}${d.pinned ? " · pinned" : ""}`}
          className={
            "rounded-full border px-2 py-0.5 text-[10px] font-medium " +
            (d.pinned
              ? "border-primary/40 bg-primary/10 text-primary"
              : "border-border bg-muted text-muted-foreground")
          }
        >
          {d.title}
          {d.pinned ? " ★" : ""}
        </span>
      ))}
      {pinnedMissedTripwire ? (
        <span className="ml-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] font-medium text-amber-700 dark:text-amber-300">
          ⚠ {pinnedDocCount} pinned, 0 hits
        </span>
      ) : null}
    </div>
  );
};
